<template>
	<transition name="dialog-fade">
		<div v-if="visible" class="dialog-overlay" @click="handleOverlayClick">
			<div class="dialog-container" @click.stop :style="containerStyle">
				<!-- 标题区域 -->
				<div class="dialog-header" :style="{ paddingTop: titleTopSpacing + 'px' }">
					<h3 class="dialog-title">{{ title }}</h3>
				</div>

				<!-- 内容区域 -->
				<div
					class="dialog-content"
					:style="{
                        paddingLeft: contentHorizontalSpacing + 'px',
                        paddingRight: contentHorizontalSpacing + 'px',
                        paddingBottom: contentBottomSpacing + 'px'
                    }"
				>
					<slot>{{ content }}</slot>
				</div>

				<!-- 分割线 -->
				<div class="dialog-divider"></div>

				<!-- 按钮区域 -->
				<div class="dialog-footer" :class="{ 'single-button': !showCancel }">
					<button
						v-if="showCancel"
						class="dialog-btn btn-cancel"
						@click="handleCancel"
					>
						{{ cancelText }}
					</button>
					<button
						class="dialog-btn btn-confirm"
						@click="handleConfirm"
					>
						{{ confirmText }}
					</button>
				</div>
			</div>
		</div>
	</transition>
</template>

<script>
export default {
	name: 'ConfirmPopUp',
	props: {
		// 是否显示弹窗
		visible: {
			type: Boolean,
			default: false
		},
		// 标题
		title: {
			type: String,
			default: '提示'
		},
		// 内容（可以用 slot 替代）
		content: {
			type: String,
			default: ''
		},
		// 确认按钮文字
		confirmText: {
			type: String,
			default: '确认提交'
		},
		// 取消按钮文字
		cancelText: {
			type: String,
			default: '取消'
		},
		// 是否显示取消按钮
		showCancel: {
			type: Boolean,
			default: true
		},
		// 标题距上边框的距离
		titleTopSpacing: {
			type: Number,
			default: 20
		},
		// 内容距下面按钮区分割线的距离
		contentBottomSpacing: {
			type: Number,
			default: 18
		},
		// 内容距离左右两边边框的距离
		contentHorizontalSpacing: {
			type: Number,
			default: 24
		},
		// 点击遮罩层是否关闭
		closeOnClickOverlay: {
			type: Boolean,
			default: true
		},
		// 距离屏幕两侧的距离（优先级高于 widthPercent）
		sideSpacing: {
			type: Number,
			default: 52
		},
		// 宽度占屏幕的百分比（当 sideSpacing 为 null 时生效）
		widthPercent: {
			type: Number,
			default: null,
			validator: (value) => value === null || (value > 0 && value <= 100)
		},
		// 固定宽度（当 sideSpacing 和 widthPercent 都为 null 时生效）
		width: {
			type: String,
			default: '400px'
		}
	},
	computed: {
		containerStyle() {
			const styles = {};
			if (this.sideSpacing !== null) {
				// 优先使用 sideSpacing
				styles.width = `calc(100vw - ${this.sideSpacing * 2}px)`;
				styles.maxWidth = `calc(100vw - ${this.sideSpacing * 2}px)`;
			} else if (this.widthPercent !== null) {
				// 其次使用 widthPercent
				styles.width = `${this.widthPercent}vw`;
				styles.maxWidth = `${this.widthPercent}vw`;
			} else {
				// 最后使用 width
				styles.width = this.width;
				styles.maxWidth = '90vw';
			}

			return styles;
		}
	},
	methods: {
		handleConfirm() {
			this.$emit('confirm');
			this.close();
		},
		handleCancel() {
			this.$emit('cancel');
			this.close();
		},
		handleOverlayClick() {
			if (this.closeOnClickOverlay) {
				this.$emit('cancel');
				this.close();
			}
		},
		close() {
			this.$emit('update:visible', false);
		}
	}
}
</script>

<style scoped>
/* 遮罩层 */
.dialog-overlay {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: rgba(0, 0, 0, 0.5);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 2000;
}

/* 弹窗容器 */
.dialog-container {
	background: #fff;
	border-radius: 8px;
	box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
	overflow: hidden;
}

/* 标题区域 */
.dialog-header {
	padding-left: 32px;
	padding-right: 32px;
	padding-bottom: 16px;
	font-size: 17px;
	line-height: 24px;
	color: #141E33;
}

.dialog-title {
	margin: 0;
	font-size: 18px;
	font-weight: 500;
	color: #333;
	text-align: center;
}

/* 内容区域 */
.dialog-content {
	padding-top: 0;
	font-size: 14px;
	line-height: 20px;
	color: #141E33;
	text-align: center;
	word-break: break-word;
}

/* 分割线 */
.dialog-divider {
	height: 1px;
	background: #EAECF0;
}

/* 按钮区域 */
.dialog-footer {
	display: flex;
	height: 44px;
}

.dialog-footer.single-button {
	display: block;
}

/* 按钮样式 */
.dialog-btn {
	flex: 1;
	height: 100%;
	border: none;
	background: none;
	font-size: 16px;
	cursor: pointer;
	transition: background-color 0.3s;
	outline: none;
}

.dialog-footer.single-button .dialog-btn {
	width: 100%;
	font-size: 17px;
	line-height: 24px;
}

.btn-cancel {
	color: #7A8297;
	border-right: 1px solid #E5E5E5;
}

.btn-cancel:hover {
	background: #F5F5F5;
}

.btn-cancel:active {
	background: #E8E8E8;
}

.btn-confirm {
	color: #007AFD;
	font-weight: 500;
}

.btn-confirm:hover {
	background: #F0F7FF;
}

.btn-confirm:active {
	background: #E0EFFF;
}

/* 动画效果 */
.dialog-fade-enter-active,
.dialog-fade-leave-active {
	transition: opacity 0.3s;
}

.dialog-fade-enter-active .dialog-container,
.dialog-fade-leave-active .dialog-container {
	transition: transform 0.3s;
}

.dialog-fade-enter,
.dialog-fade-leave-to {
	opacity: 0;
}

.dialog-fade-enter .dialog-container,
.dialog-fade-leave-to .dialog-container {
	transform: scale(0.9);
}

/* 移动端适配 */
@media (max-width: 768px) {
	.dialog-title {
		font-size: 17px;
	}

	.dialog-content {
		font-size: 15px;
		line-height: 22px;
	}

	.dialog-btn {
		font-size: 15px;
	}
}
</style>



// WEBPACK FOOTER //
// src/components/ConfirmPopUp.vue